import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DollarSign, Zap, Activity, TrendingDown, ArrowUpRight, ArrowDownRight, Clock } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ProxyAnalytics() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);

  const fetch = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/proxy-keys/status`),
        axios.get(`${API}/proxy-keys/logs?limit=20`),
      ]);
      setStats(s.data);
      setLogs(l.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetch(); const iv = setInterval(fetch, 15000); return () => clearInterval(iv); }, [fetch]);

  if (!stats?.has_key) return null;

  const saved = stats.total_saved || 0;
  const original = stats.total_original_cost || 0;
  const optimized = stats.total_optimized_cost || 0;
  const savingsPct = original > 0 ? ((saved / original) * 100).toFixed(1) : "0.0";
  const totalReqs = stats.total_requests || 0;

  return (
    <div className="space-y-4" data-testid="proxy-analytics" data-automation-id="proxy-analytics">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Would Have Spent", value: `$${original.toFixed(2)}`, icon: DollarSign, color: "var(--qn-foreground-muted)" },
          { label: "Actually Spent", value: `$${optimized.toFixed(2)}`, icon: TrendingDown, color: "var(--qn-primary)" },
          { label: "Total Saved", value: `$${saved.toFixed(2)}`, icon: Zap, color: "var(--qn-success)", glow: true },
          { label: "Requests Optimized", value: totalReqs.toLocaleString(), icon: Activity, color: "var(--qn-secondary)" },
        ].map((c, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <c.icon className="w-4 h-4" style={{ color: c.color }} />
                  <span className="text-xs text-[var(--qn-foreground-muted)]">{c.label}</span>
                </div>
                <div className="font-mono font-bold text-xl" style={{ color: c.color, textShadow: c.glow ? "0 0 12px rgba(16,185,129,0.3)" : "none" }} data-testid={`proxy-stat-${i}`}>
                  {c.value}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Savings Rate */}
      {totalReqs > 0 && (
        <div className="rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)] px-4 py-3 flex items-center justify-between" data-testid="proxy-savings-rate" data-automation-id="proxy-savings-rate">
          <span className="text-sm text-[var(--qn-foreground-muted)]">Average Savings Rate</span>
          <span className="font-mono font-bold text-lg text-[var(--qn-success)]">{savingsPct}%</span>
        </div>
      )}

      {/* Request Log */}
      {logs.length > 0 && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4 text-[var(--qn-foreground-muted)]" />
              Recent Proxy Requests
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="proxy-request-log" data-automation-id="proxy-request-log">
                <thead>
                  <tr className="border-b border-[var(--qn-border)] bg-[var(--qn-surface)]">
                    <th className="text-left px-4 py-2 text-xs font-medium text-[var(--qn-foreground-muted)]">Time</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-[var(--qn-foreground-muted)]">Requested</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-[var(--qn-foreground-muted)]">Routed To</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-[var(--qn-foreground-muted)]">Original</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-[var(--qn-foreground-muted)]">QNNEAL</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-[var(--qn-foreground-muted)]">Saved</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-[var(--qn-foreground-muted)]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={i} className="border-b border-[var(--qn-border)] last:border-0 hover:bg-[var(--qn-surface-hover)] transition-colors">
                      <td className="px-4 py-2.5 text-xs text-[var(--qn-foreground-subtle)]">{new Date(log.timestamp).toLocaleTimeString()}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{log.requested_model}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">
                        <span className="text-[var(--qn-primary)]">{log.routed_model}</span>
                        {log.routed_model !== log.requested_model && <ArrowUpRight className="w-3 h-3 inline ml-1 text-[var(--qn-success)]" />}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">${(log.original_cost || 0).toFixed(4)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-[var(--qn-primary)]">${(log.optimized_cost || 0).toFixed(4)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-[var(--qn-success)]">
                        {(log.savings_percent || 0) > 0 ? (
                          <span className="flex items-center justify-end gap-0.5"><ArrowDownRight className="w-3 h-3" />{log.savings_percent}%</span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Badge variant="outline" className={`text-[10px] ${log.status === "success" ? "text-emerald-400 border-emerald-400/30" : "text-red-400 border-red-400/30"}`}>
                          {log.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
